import React, { ChangeEvent, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import {
  SBox,
  STextField,
  SLinkContainer,
  SStaticText,
  SDivider,
  STitle,
  SForm,
  STextFieldWrapper,
  STextFieldBackground,
  SLabel,
  SClickableTextBlue,
  SClickableTextRegister,
  SCustomHelperText,
  SLableInputCode,
  SResendText,
} from './styles';
import Texts from '../../Texts';
import { validationSchema } from './validation';
import ArrowForwardIcon from '@mui/icons-material/ArrowCircleRight';
import ArrowForwardLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import { useDispatch, useSelector } from 'react-redux';
import {
  verifyCode,
  sendMobileNumber,
  setIsRegistered,
  setError,
} from '../../../Slice/auth/authSlice';
import { RootState, AppDispatch } from '../../../store';
import { Button, Alert } from '@mui/material'; // اضافه کردن Alert

import { grey, blue } from '@mui/material/colors';
import CircularTimer from './CircularTimer';
import { useNavigate } from 'react-router-dom';
import NumberConverter from '../../../helper/ConvertPersionNumberToEnglish';
import { incrementStep } from '../../../Slice/unifiedFormSlice';
import { ACTION_BLUE, ACTION_BLUE_HOVER } from '../../../theme/theme';

const SMSCodeVerification: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { mobile, ttl, loading, token, publishers, recaptchaToken, error } =
    useSelector((state: RootState) => state.auth);
  const [code, setCode] = useState('');
  const [timerKey, setTimerKey] = useState<number>(0);
  const [canResend, setCanResend] = useState<boolean>(false);

  const formik = useFormik({
    initialValues: { code: '' },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const englishCode = NumberConverter.toEnglishNumber(values.code);
      dispatch(verifyCode({ mobile, code: englishCode }));
    },
  });

  const handleNavigation = () => {
    console.log('Navigating with publishers:', publishers);
    if (publishers.length < 1) {
      navigate('/wizard', { state: { publishers, mode: 'start' } });
    } else if (publishers.length >= 1) {
      navigate('/dashboard', {
        state: { publishers, mode: 'start' },
      });
    }
  };

  useEffect(() => {
    if (token) {
      handleNavigation();
    }
  }, [token, publishers, navigate, dispatch]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(setError(null));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const persianCode = NumberConverter.toPersianNumber(value);

    if (persianCode.length <= 6) {
      setCode(persianCode);
      formik.setFieldValue('code', persianCode);
    }
  };

  const formatPlaceholder = (value: string) => {
    const placeholder = '- - - - - -'; // برای ۶ رقم
    return placeholder
      .split('')
      .map((char, index) =>
        index % 2 === 0 && value[index / 2] ? value[index / 2] : char,
      )
      .join('');
  };

  const handleResend = async () => {
    setCanResend(false);
    setTimerKey((prevKey) => prevKey + 1);

    try {
      await dispatch(sendMobileNumber({ mobile, recaptchaToken })).unwrap();
    } catch {
      // No new code is on its way, so offer the link again rather than
      // leaving the user watching a timer for an sms that was never sent.
      // The message itself is surfaced by the effect watching `error`.
      setCanResend(true);
    }
  };

  const handleTimerComplete = () => {
    setCanResend(true);
  };

  return (
    <SBox>
      <SForm onSubmit={formik.handleSubmit}>
        <Button
          variant="contained"
          onClick={() => dispatch(setIsRegistered(false))}
          endIcon={
            <ArrowForwardIcon
              style={{
                paddingRight: '0px',
                color: 'gray',
                width: '16px',
                height: '16px',
                marginLeft: '1px',
                marginRight: '1px',
              }}
            />
          }
          sx={{
            width: '128px',
            height: '36px',
            marginBottom: '8px',
            backgroundColor: '#FFFFFF',
            color: '#35373C',
            fontSize: '11px',
            fontFamily: 'IRANYekanXFaNum',
            fontWeight: 500,
            lineHeight: '16px',
            padding: '1px',
            textTransform: 'none',
            wordWrap: 'break-word',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: grey[200],
              boxShadow: 'none',
            },
          }}
        >
          {Texts.Verification.buttonEditNumber}
        </Button>

        <STitle>{Texts.Verification.title}</STitle>
        <SLableInputCode>{Texts.Verification.subTitle}</SLableInputCode>

        <SLabel>{Texts.Verification.smsCode}</SLabel>

        <STextFieldWrapper>
          <STextFieldBackground />
          <STextField
            id="code"
            name="code"
            type="text"
            placeholder={formatPlaceholder(code)}
            variant="outlined"
            value={formik.values.code}
            onChange={handleCodeChange}
            onBlur={formik.handleBlur}
            error={formik.touched.code && Boolean(formik.errors.code)}
            helperText={
              formik.touched.code && (
                <SCustomHelperText error={Boolean(formik.errors.code)}>
                  {formik.errors.code}
                </SCustomHelperText>
              )
            }
            inputProps={{
              maxLength: 6,
            }}
          />
        </STextFieldWrapper>

        {/* نمایش پیام خطای سرور */}
        {error && (
          <Alert
            severity="error"
            sx={{
              marginTop: '2px',
              width: '100%',
              fontFamily: 'IRANYekanXFaNum',
              fontSize: '14px',
              textAlign: 'right',
              direction: 'rtl',
              fontWeight: 500,
              lineHeight: '16px',
              backgroundColor: '#F8D7DA',
              color: '#d31728',
            }}
          >
            {typeof error === 'string' ? error : 'خطای نامشخص'}
          </Alert>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            marginTop: '8px',
            alignItems: 'center',
          }}
        >
          <Button
            variant="contained"
            disabled={loading}
            type="submit"
            startIcon={<ArrowForwardLeftIcon />}
            sx={{
              width: '170px',
              height: '44px',
              marginBottom: '16px',
              backgroundColor: ACTION_BLUE,
              color: '#FFFFFF',
              fontSize: '14px',
              fontFamily: 'IRANYekanXFaNum',
              fontWeight: 700,
              lineHeight: '16px',
              marginTop: '8px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: ACTION_BLUE_HOVER,
              },
            }}
          >
            {Texts.Verification.buttonAcceptCode}
          </Button>

          <div>
            {!canResend ? (
              <SLabel>
                <SClickableTextRegister>
                  <CircularTimer
                    key={timerKey}
                    totalTime={ttl}
                    onComplete={handleTimerComplete}
                  />
                </SClickableTextRegister>
              </SLabel>
            ) : (
              <SLabel>
                <SResendText onClick={handleResend}>
                  {Texts.Verification.resendCode}
                </SResendText>
              </SLabel>
            )}
          </div>
        </div>

        <SDivider />
        <SLinkContainer>
          <SClickableTextBlue>{Texts.Register.support}</SClickableTextBlue>
          <SStaticText>{Texts.Register.helpMe}</SStaticText>
        </SLinkContainer>
      </SForm>
    </SBox>
  );
};

export default SMSCodeVerification;
